"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { gsap } from "gsap";
import { cn } from "@/lib/cn";
import { lockScroll, unlockScroll } from "@/lib/scroll-lock";
import { ctas, primaryNav, site } from "@/content/site";
import { Wordmark } from "./wordmark";

export function SiteHeader() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);

  // The overlay records the route it was opened on. Navigating anywhere else
  // closes it by derivation, so no effect has to chase the pathname.
  const [openedOn, setOpenedOn] = useState<string | null>(null);
  const open = openedOn === pathname;

  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Every page opens on a navy hero, so the header is light-on-dark until the
  // reader scrolls past it — and again whenever the navy menu overlay is up,
  // whatever the scroll position.
  const onDark = open || !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = useCallback(() => setOpenedOn(null), []);

  useEffect(() => {
    if (!open) return;

    lockScroll();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        triggerRef.current?.focus();
        return;
      }

      if (event.key !== "Tab") return;

      // Keep focus inside the overlay while it is open.
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      );
      if (!focusables?.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    const ctx = gsap.context(() => {
      gsap
        .timeline({ defaults: { ease: "power3.out" } })
        .fromTo(
          "[data-menu-panel]",
          { yPercent: -100 },
          { yPercent: 0, duration: 0.6, ease: "power4.inOut" },
        )
        .from(
          "[data-menu-item]",
          { yPercent: 110, opacity: 0, duration: 0.7, stagger: 0.055 },
          "-=0.22",
        )
        .from("[data-menu-meta]", { opacity: 0, y: 14, duration: 0.5 }, "-=0.35");
    }, panelRef);

    return () => {
      ctx.revert();
      document.removeEventListener("keydown", onKeyDown);
      unlockScroll();
    };
  }, [open, close]);

  return (
    <>
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]",
          scrolled && !open
            ? "border-b border-ink-900/10 bg-paper/85 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent",
        )}
      >
        <div
          className={cn(
            "shell-wide flex items-center justify-between transition-[height] duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]",
            scrolled ? "h-[4.25rem] md:h-[4.75rem]" : "h-[4.5rem] md:h-[5.5rem]",
          )}
        >
          <Wordmark tone={onDark ? "paper" : "ink"} />

          <nav
            aria-label="Primary"
            className="hidden items-center gap-9 lg:flex"
          >
            {primaryNav.map((item) => {
              const active =
                pathname === item.href || pathname.startsWith(`${item.href}/`);

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "group/nav relative py-1 text-[0.8125rem] font-medium tracking-[0.02em] transition-colors duration-300",
                    onDark
                      ? "text-ink-100 hover:text-sky-400"
                      : "text-ink-700 hover:text-ink-950",
                    active && (onDark ? "text-sky-400" : "text-ink-950"),
                  )}
                >
                  {item.label}
                  <span
                    className={cn(
                      "absolute inset-x-0 -bottom-0.5 h-px origin-left scale-x-0 bg-sky-500 transition-transform duration-400 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover/nav:scale-x-100",
                      active && "scale-x-100",
                    )}
                  />
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              href={ctas.primary.href}
              className={cn(
                "hidden min-h-10 items-center px-5 text-[0.75rem] font-medium tracking-[0.08em] uppercase transition-colors duration-300 lg:inline-flex",
                onDark
                  ? "bg-sky-500 text-ink-950 hover:bg-paper"
                  : "bg-ink-900 text-paper hover:bg-sky-500 hover:text-ink-950",
              )}
            >
              {ctas.primary.label}
            </Link>

            <button
              ref={triggerRef}
              type="button"
              onClick={() => setOpenedOn(open ? null : pathname)}
              aria-expanded={open}
              aria-controls="site-menu"
              className={cn(
                "-mr-2 flex h-11 w-11 items-center justify-center lg:hidden",
                onDark ? "text-paper" : "text-ink-900",
              )}
            >
              <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>
              <span className="relative block h-3 w-6" aria-hidden="true">
                <span
                  className={cn(
                    "absolute left-0 block h-px w-full bg-current transition-transform duration-400 ease-[cubic-bezier(0.83,0,0.17,1)]",
                    open ? "top-1/2 rotate-45" : "top-0",
                  )}
                />
                <span
                  className={cn(
                    "absolute left-0 block h-px w-full bg-current transition-transform duration-400 ease-[cubic-bezier(0.83,0,0.17,1)]",
                    open ? "top-1/2 -rotate-45" : "top-full",
                  )}
                />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile / tablet overlay */}
      {open ? (
        <div
          id="site-menu"
          ref={panelRef}
          className="fixed inset-0 z-40 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
        >
          <div
            data-menu-panel
            className="grain relative flex h-full flex-col overflow-y-auto bg-ink-950 pt-[5.5rem] pb-10"
          >
            <nav aria-label="Mobile" className="shell flex-1">
              <ul>
                {primaryNav.map((item) => (
                  <li
                    key={item.href}
                    className="overflow-hidden border-b border-paper/10"
                  >
                    <Link
                      href={item.href}
                      data-menu-item
                      className="group/link flex items-baseline gap-4 py-5"
                    >
                      <span
                        aria-hidden="true"
                        className="mt-3.5 h-px w-5 shrink-0 self-start bg-sky-500"
                      />
                      <span className="flex-1">
                        <span className="block font-[family-name:var(--font-display)] text-[1.75rem] leading-tight text-paper transition-colors duration-300 group-hover/link:text-sky-400">
                          {item.label}
                        </span>
                        {item.description ? (
                          <span className="mt-1 block text-[0.8125rem] text-ink-300">
                            {item.description}
                          </span>
                        ) : null}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div data-menu-meta className="shell mt-10 space-y-5">
              <Link
                href={ctas.primary.href}
                className="flex min-h-12 w-full items-center justify-center bg-sky-500 px-6 text-[0.8125rem] font-medium tracking-[0.08em] text-ink-950 uppercase"
              >
                {ctas.primary.label}
              </Link>

              <div className="space-y-1 border-t border-paper/10 pt-5">
                {site.contact.email ? (
                  <a
                    href={`mailto:${site.contact.email}`}
                    className="block text-sm text-ink-200 underline-offset-4 hover:text-sky-400 hover:underline"
                  >
                    {site.contact.email}
                  </a>
                ) : null}
                <p className="text-[0.8125rem] text-ink-400">
                  {site.contact.location}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
